import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import VenueSelector from '../components/VenueSelector';
import HostSelector from '../components/HostSelector';
import { saveRecurringGameInstances, updateRecurringGameSchedule } from '../utils/gameSync';
import { generateAllSampleData } from '../utils/sampleData';

const AdminPublicGames = () => {
  const navigate = useNavigate();
  const [scheduledGames, setScheduledGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGame, setEditingGame] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    gameTitle: '',
    gameDescription: '',
    venueName: '',
    venueAddress: '',
    venueLocation: '',
    hostName: '',
    hostId: '',
    isActive: true,
    isRecurring: true,
    recurringDays: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    },
    recurringTime: ''
  });
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  const [selectedGames, setSelectedGames] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    loadScheduledGames();
  }, []);

  const loadScheduledGames = async () => {
    try {
      setLoading(true);
      
      let games = [];
      
      // First try to load from the new collection
      try {
        const gamesQuery = query(
          collection(db, 'scheduledGames'),
          orderBy('scheduledDateTime', 'desc')
        );
        const snapshot = await getDocs(gamesQuery);
        games = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Loaded games from scheduledGames collection:', games.length);
      } catch (error) {
        console.log('No games in scheduledGames collection:', error.message);
      }
      
      // If no games found, try to load from the old collection structure
      if (games.length === 0) {
        try {
          // Try to create parent document and access subcollection
          const parentDocRef = doc(db, 'games', 'scheduled');
          await setDoc(parentDocRef, { 
            createdAt: new Date(),
            updatedAt: new Date()
          }, { merge: true });
          
          const oldGamesQuery = query(
            collection(db, 'games', 'scheduled', 'games'),
            orderBy('scheduledDateTime', 'desc')
          );
          const oldSnapshot = await getDocs(oldGamesQuery);
          const oldGames = oldSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('Loaded games from old collection structure:', oldGames.length);
          
          // Migrate old games to new collection
          for (const game of oldGames) {
            try {
              await addDoc(collection(db, 'scheduledGames'), game);
              console.log('Migrated game:', game.gameTitle);
            } catch (migrateError) {
              console.log('Failed to migrate game:', migrateError.message);
            }
          }
          
          games = oldGames;
        } catch (oldError) {
          console.log('Could not load from old collection structure:', oldError.message);
        }
      }
      
      setScheduledGames(games);
    } catch (error) {
      console.error('Error loading scheduled games:', error);
      setScheduledGames([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRecurringDayChange = (day, checked) => {
    setFormData(prev => ({
      ...prev,
      recurringDays: {
        ...prev.recurringDays,
        [day]: checked
      }
    }));
  };

  const generateRecurringGames = (baseGameData) => {
    // Create a single recurring game entry
    const recurringGameData = {
      ...baseGameData,
      isRecurring: true,
      recurringDays: baseGameData.recurringDays,
      recurringTime: baseGameData.recurringTime,
      // Keep the original scheduled date as the "start date" for the recurring schedule
      recurringStartDate: new Date(baseGameData.scheduledDateTime)
    };
    
    return [recurringGameData];
  };

  const handleVenueSelect = (venue) => {
    setSelectedVenue(venue);
    if (venue) {
      // Extract city and state from the address
      const addressParts = venue.address.split(', ');
      const cityState = addressParts.slice(-2).join(', '); // Last two parts are usually city, state
      
      setFormData(prev => ({
        ...prev,
        venueName: venue.name || '',
        venueAddress: venue.address || '',
        venueLocation: cityState || venue.location ? `${venue.location.lat}, ${venue.location.lng}` : ''
      }));
    }
  };

  const handleHostSelect = (host) => {
    setSelectedHost(host);
    if (host) {
      setFormData(prev => ({
        ...prev,
        hostName: host.fullName || host.email || '',
        hostId: host.id || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const gameData = {
        ...formData,
        status: formData.isActive ? 'scheduled' : 'inactive',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingGame) {
        // Check if this is a recurring game
        if (editingGame.isRecurring) {
          // Update recurring game schedule and regenerate instances
          await updateRecurringGameSchedule(editingGame.id, gameData);
          alert('Updated recurring game schedule and regenerated instances!');
        } else {
          // Regular game update
          await updateDoc(doc(db, 'scheduledGames', editingGame.id), {
            ...gameData,
            updatedAt: new Date()
          });
        }
      } else {
        // Always create as recurring game
        const recurringGames = generateRecurringGames(gameData);
        
        // Save the parent recurring game first
        const parentGame = recurringGames[0];
        const parentDocRef = await addDoc(collection(db, 'scheduledGames'), parentGame);
        
        // Generate and save actual game instances
        const parentGameWithId = { ...parentGame, id: parentDocRef.id };
        const instances = await saveRecurringGameInstances(parentGameWithId, 8); // 8 weeks ahead
        
        alert(`Created weekly game successfully! Generated ${instances.length} game instances.`);
      }

      setFormData({
        gameTitle: '',
        gameDescription: '',
        venueName: '',
        venueAddress: '',
        venueLocation: '',
        hostName: '',
        hostId: '',
        isActive: true,
        isRecurring: true,
        recurringDays: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        },
        recurringTime: ''
      });
      setSelectedVenue(null);
      setSelectedHost(null);
      setEditingGame(null);
      setShowAddForm(false);
      loadScheduledGames();
    } catch (error) {
      console.error('Error saving game:', error);
      alert('Error saving game. Please try again.');
    }
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setFormData({
      gameTitle: game.gameTitle || '',
      gameDescription: game.gameDescription || '',
      venueName: game.venueName || '',
      venueAddress: game.venueAddress || '',
      venueLocation: game.venueLocation || '',
      hostName: game.hostName || '',
      hostId: game.hostId || '',
      isActive: game.status !== 'inactive',
      isRecurring: game.isRecurring || true,
      recurringDays: game.recurringDays || {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
      },
      recurringTime: game.recurringTime || ''
    });
    
    // Set selected venue for editing
    if (game.venueName) {
      setSelectedVenue({
        name: game.venueName,
        address: game.venueAddress,
        location: game.venueLocation
      });
    } else {
      setSelectedVenue(null);
    }

    // Set selected host for editing
    if (game.hostId) {
      setSelectedHost({
        id: game.hostId,
        fullName: game.hostName
      });
    } else {
      setSelectedHost(null);
    }
    
    setShowAddForm(true);
  };

  const handleDelete = async (gameId) => {
    if (window.confirm('Are you sure you want to delete this scheduled game?')) {
      try {
        await deleteDoc(doc(db, 'scheduledGames', gameId));
        loadScheduledGames();
      } catch (error) {
        console.error('Error deleting game:', error);
        alert('Error deleting game. Please try again.');
      }
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedGames([]);
      setSelectAll(false);
    } else {
      setSelectedGames(scheduledGames.map(game => game.id));
      setSelectAll(true);
    }
  };

  const handleSelectGame = (gameId) => {
    setSelectedGames(prev => {
      if (prev.includes(gameId)) {
        const newSelected = prev.filter(id => id !== gameId);
        setSelectAll(newSelected.length === scheduledGames.length);
        return newSelected;
      } else {
        const newSelected = [...prev, gameId];
        setSelectAll(newSelected.length === scheduledGames.length);
        return newSelected;
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedGames.length === 0) {
      alert('Please select games to delete.');
      return;
    }

    const confirmMessage = selectedGames.length === 1 
      ? 'Are you sure you want to delete this game?' 
      : `Are you sure you want to delete ${selectedGames.length} games?`;

    if (window.confirm(confirmMessage)) {
      try {
        const deletePromises = selectedGames.map(gameId => 
          deleteDoc(doc(db, 'scheduledGames', gameId))
        );
        await Promise.all(deletePromises);
        setSelectedGames([]);
        setSelectAll(false);
        loadScheduledGames();
        alert(`Successfully deleted ${selectedGames.length} game(s)!`);
      } catch (error) {
        console.error('Error deleting games:', error);
        alert('Error deleting games. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setEditingGame(null);
    setShowAddForm(false);
    setFormData({
      gameTitle: '',
      gameDescription: '',
      venueName: '',
      venueAddress: '',
      venueLocation: '',
      scheduledDateTime: '',
      hostName: '',
      isActive: true
    });
    setSelectedVenue(null);
  };

  const createSampleGames = async () => {
    try {
      console.log('🎯 Generating sample data for testing...');
      const result = await generateAllSampleData();
      
      alert(`Sample data created successfully!\n📊 ${result.teams.length} teams\n🏆 ${result.results.length} game results\n🎮 ${result.games.length} recurring games`);
      loadScheduledGames();
    } catch (error) {
      console.error('Error creating sample data:', error);
      alert('Error creating sample data. Please try again.');
    }
  };

  const searchAllCollections = async () => {
    try {
      console.log('🔍 Searching all possible collections for games...');
      
      const collectionsToSearch = [
        'scheduledGames',
        'games',
        'scheduled',
        'publicGames',
        'gameListings'
      ];
      
      for (const collectionName of collectionsToSearch) {
        try {
          const query = collection(db, collectionName);
          const snapshot = await getDocs(query);
          const games = snapshot.docs.map(doc => ({
            id: doc.id,
            collection: collectionName,
            ...doc.data()
          }));
          
          if (games.length > 0) {
            console.log(`📁 Found ${games.length} games in collection '${collectionName}':`, games);
          }
        } catch (error) {
          console.log(`❌ Could not access collection '${collectionName}':`, error.message);
        }
      }
      
      // Also try subcollections
      try {
        const subcollections = [
          ['games', 'scheduled'],
          ['games', 'public'],
          ['public', 'games'],
          ['scheduled', 'games']
        ];
        
        for (const [parent, child] of subcollections) {
          try {
            const query = collection(db, parent, child);
            const snapshot = await getDocs(query);
            const games = snapshot.docs.map(doc => ({
              id: doc.id,
              collection: `${parent}/${child}`,
              ...doc.data()
            }));
            
            if (games.length > 0) {
              console.log(`📁 Found ${games.length} games in subcollection '${parent}/${child}':`, games);
            }
          } catch (error) {
            console.log(`❌ Could not access subcollection '${parent}/${child}':`, error.message);
          }
        }
      } catch (error) {
        console.log('❌ Error searching subcollections:', error.message);
      }
      
    } catch (error) {
      console.error('❌ Error searching collections:', error);
    }
  };

  // const formatDate = (dateString) => { // Removed unused function
  //   if (!dateString) return 'TBD';
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString();
  // };

  // const formatTime = (timeString) => { // Removed unused function
  //   if (!timeString) return 'TBD';
  //   const time = new Date(timeString);
  //   return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  // };

  const formatRecurringSchedule = (game) => {
    if (!game.isRecurring || !game.recurringDays || !game.recurringTime) {
      return 'TBD';
    }

    const selectedDays = Object.entries(game.recurringDays)
      .filter(([_, checked]) => checked)
      .map(([day, _]) => day.charAt(0).toUpperCase() + day.slice(1));

    if (selectedDays.length === 0) {
      return 'TBD';
    }

    // Format time (HH:MM to 12-hour format)
    const [hours, minutes] = game.recurringTime.split(':').map(Number);
    const timeString = new Date(2000, 0, 1, hours, minutes).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `Every ${selectedDays.join(', ')} at ${timeString}`;
  };

  // const formatRecurringDate = (game) => { // Removed unused function
  //   if (!game.isRecurring) {
  //     return formatDate(game.scheduledDateTime);
  //   }
  //   
  //   if (game.recurringStartDate) {
  //     return `Starting ${formatDate(game.recurringStartDate)}`;
  //   }
  //   
  //   return 'Recurring';
  // };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading scheduled games...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Manage Public Game Listings</h1>
              <p className="text-gray-300">Edit and manage the games that appear on the public Player Hub</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              ➕ Add New Game
            </button>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="bg-gray-800 rounded-lg p-6 mb-8 border border-gray-700">
              <h2 className="text-xl font-semibold text-white mb-6">
                {editingGame ? 'Edit Game' : 'Add New Game'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Game Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Game Title *
                    </label>
                    <input
                      type="text"
                      value={formData.gameTitle}
                      onChange={(e) => handleInputChange('gameTitle', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                      required
                    />
                  </div>

                  {/* Host Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Host *
                    </label>
                    <HostSelector
                      onHostSelect={handleHostSelect}
                      selectedHost={selectedHost}
                      placeholder="Search for a host..."
                    />
                  </div>

                  {/* Venue Selection */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Venue *
                    </label>
                    <VenueSelector
                      onVenueSelect={handleVenueSelect}
                      selectedVenue={selectedVenue}
                      placeholder="Search for a venue (bar, restaurant, community center...)"
                    />
                  </div>

                  {/* Venue Name (Auto-populated) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Venue Name *
                    </label>
                    <input
                      type="text"
                      value={formData.venueName}
                      onChange={(e) => handleInputChange('venueName', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                      required
                      placeholder="Will auto-populate when venue is selected"
                    />
                  </div>

                  {/* Venue Address (Auto-populated) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Venue Address
                    </label>
                    <input
                      type="text"
                      value={formData.venueAddress}
                      onChange={(e) => handleInputChange('venueAddress', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                      placeholder="Will auto-populate when venue is selected"
                    />
                  </div>
                </div>

                {/* Selected Host Information */}
                {selectedHost && (
                  <div className="bg-gray-700 rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-medium text-gray-300 mb-3">Selected Host Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Name:</span>
                        <span className="text-white ml-2">{selectedHost.fullName || selectedHost.email}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Email:</span>
                        <span className="text-white ml-2">{selectedHost.email}</span>
                      </div>
                      {selectedHost.location && (
                        <div>
                          <span className="text-gray-400">Location:</span>
                          <span className="text-white ml-2">{selectedHost.location}</span>
                        </div>
                      )}
                      {selectedHost.gamesHosted && (
                        <div>
                          <span className="text-gray-400">Games Hosted:</span>
                          <span className="text-white ml-2">{selectedHost.gamesHosted}</span>
                        </div>
                      )}
                      {selectedHost.averageRating && (
                        <div>
                          <span className="text-gray-400">Average Rating:</span>
                          <span className="text-white ml-2">{selectedHost.averageRating.toFixed(1)} ⭐</span>
                        </div>
                      )}
                      {selectedHost.bio && (
                        <div className="col-span-2">
                          <span className="text-gray-400">Bio:</span>
                          <span className="text-white ml-2">{selectedHost.bio}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Selected Venue Information */}
                {selectedVenue && (
                  <div className="bg-gray-700 rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-medium text-gray-300 mb-3">Selected Venue Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Name:</span>
                        <span className="text-white ml-2">{selectedVenue.name}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Address:</span>
                        <span className="text-white ml-2">{selectedVenue.address}</span>
                      </div>
                      {selectedVenue.phone && (
                        <div>
                          <span className="text-gray-400">Phone:</span>
                          <span className="text-white ml-2">{selectedVenue.phone}</span>
                        </div>
                      )}
                      {selectedVenue.website && (
                        <div>
                          <span className="text-gray-400">Website:</span>
                          <a href={selectedVenue.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 ml-2 hover:underline">
                            {selectedVenue.website}
                          </a>
                        </div>
                      )}
                      {selectedVenue.location && selectedVenue.location.lat && selectedVenue.location.lng && (
                        <div className="col-span-2">
                          <span className="text-gray-400">Coordinates:</span>
                          <span className="text-white ml-2">
                            {selectedVenue.location.lat.toFixed(6)}, {selectedVenue.location.lng.toFixed(6)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Weekly Game Schedule */}
                <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                  <h3 className="text-lg font-medium text-white mb-4">Weekly Game Schedule</h3>
                  
                  {/* Recurring Days */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Game Days *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries({
                        monday: 'Monday',
                        tuesday: 'Tuesday', 
                        wednesday: 'Wednesday',
                        thursday: 'Thursday',
                        friday: 'Friday',
                        saturday: 'Saturday',
                        sunday: 'Sunday'
                      }).map(([day, label]) => (
                        <label key={day} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.recurringDays[day]}
                            onChange={(e) => handleRecurringDayChange(day, e.target.checked)}
                            className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-300">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Game Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Game Time *
                    </label>
                    <input
                      type="time"
                      value={formData.recurringTime}
                      onChange={(e) => handleInputChange('recurringTime', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                      required
                    />
                  </div>

                  {/* Preview */}
                  {formData.recurringTime && Object.values(formData.recurringDays).some(day => day) && (
                    <div className="bg-gray-800 rounded-lg p-3 mt-4">
                      <h4 className="text-sm font-medium text-gray-300 mb-2">Preview:</h4>
                      <p className="text-sm text-gray-400">
                        This will create a weekly game every{' '}
                        {Object.entries(formData.recurringDays)
                          .filter(([_, checked]) => checked)
                          .map(([day, _]) => day.charAt(0).toUpperCase() + day.slice(1))
                          .join(', ')}{' '}
                        at {formData.recurringTime}
                      </p>
                    </div>
                  )}
                </div>

                {/* Game Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Game Description
                  </label>
                  <textarea
                    value={formData.gameDescription}
                    onChange={(e) => handleInputChange('gameDescription', e.target.value)}
                    rows={3}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                    placeholder="Optional description of the game..."
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                  />
                  <label htmlFor="isActive" className="ml-2 text-sm text-gray-300">
                    Active (visible on Player Hub)
                  </label>
                </div>

                {/* Form Actions */}
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                  >
                    {editingGame ? 'Update Game' : 'Add Game'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Games List */}
          <div className="bg-gray-800 rounded-lg shadow-md">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Scheduled Games ({scheduledGames.length})</h2>
              <div className="flex space-x-2">
                {selectedGames.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                  >
                    Delete Selected ({selectedGames.length})
                  </button>
                )}
                <button
                  onClick={searchAllCollections}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Search All Collections
                </button>
                <button
                  onClick={createSampleGames}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Create Sample Games
                </button>
                <button
                  onClick={loadScheduledGames}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Game
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Venue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Schedule
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Host
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {scheduledGames.length > 0 ? (
                    scheduledGames.map((game) => (
                      <tr key={game.id} className="hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedGames.includes(game.id)}
                            onChange={() => handleSelectGame(game.id)}
                            className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-white flex items-center gap-2">
                              {game.gameTitle || 'Untitled Game'}
                              {game.isRecurring && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                  🔄 Recurring
                                </span>
                              )}
                            </div>
                            {game.gameDescription && (
                              <div className="text-sm text-gray-400 truncate max-w-xs">
                                {game.gameDescription}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-white">
                              {game.venueName || 'Unknown Venue'}
                            </div>
                            <div className="text-sm text-gray-400">
                              {game.venueAddress || game.venueLocation}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {formatRecurringSchedule(game)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {game.hostId && game.hostName ? (
                            <button
                              onClick={() => navigate(`/profile/${game.hostId}`)}
                              className="text-blue-400 hover:text-blue-300 transition-colors underline"
                            >
                              {game.hostName}
                            </button>
                          ) : (
                            <span className="text-gray-400">{game.hostName || 'TBD'}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            game.status === 'scheduled' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {game.status === 'scheduled' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(game)}
                              className="text-blue-400 hover:text-blue-300 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(game.id)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                                          <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                      No scheduled games found. Add your first game to get started!
                    </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Back to Dashboard */}
          <div className="mt-8">
            <button
              onClick={() => navigate('/admin')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              ← Back to Admin Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPublicGames; 